import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import moment from 'moment'

import Panel from '../ui/Panel'
import Btn from '../ui/Btn'
import StatTile from '../ui/StatTile'
import VideoModal from '../ui/VideoModal'
import TimeChart, { fillMonthGaps } from '../ui/TimeChart'
import { posterUrl } from '../ui/videoSource'
import ChallengeFormReview from './ChallengeFormReview'
import './challenges.css'

/*
 * DASHBOARD — the review queue.
 *
 * GET admin/dashboard  -> [{ UUID, UserID, ChallengeID, FirstName, LastName,
 *                            Title, SportID, FileID, DateCreated }]
 * GET admin/sports     -> [{ SportID, SportName }]
 *
 * Both requests — URL, headers, and what is read out of the response — are
 * exactly what they were. Everything below is presentation.
 *
 * This screen is work to get through, not a report. The old version buried the
 * submissions in one Bootstrap tab per sport, with the sport tabs as the only
 * way to move and the video hidden behind two clicks. Now:
 *
 *   - the whole queue is one list, newest submission first, because a reviewer
 *     works the top of the pile;
 *   - the sport tabs became filter chips that keep their own counts, so an
 *     empty sport no longer costs a click to discover;
 *   - every row shows the poster frame of the clip, and the frame IS the play
 *     button — the tape opens in the shared VideoModal and the reviewer can walk
 *     the entire queue inside it without going back to the list;
 *   - "Έλεγχος" opens the same ChallengeFormReview the old row link opened, with
 *     the same props.
 *
 * ANALYTICS STRIP (added above the queue)
 *
 * GET admin/analytics/overview      -> the four figures
 * GET admin/analytics/growth?months=6 -> the registrations sparkline
 *
 * Two extra GETs, both fired once on mount and both independent of the queue:
 * they have their own state, their own failure flags and they never touch
 * `data`, `sports`, `isLoading` or `failed`. If the overview request fails the
 * whole strip is not rendered — the queue is exactly what it was. If only the
 * growth request fails, the strip stands and the chart shows its empty state
 * rather than a fabricated flat line at zero.
 *
 * The strip is deliberately short. At 1440x900 it is 168px tall (12px top pad +
 * 140px content + 14px bottom pad + 2px border) plus the page's 20px gap, which
 * leaves the first two submissions of the queue above the fold.
 */

const IconPlay = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M8 5.2v13.6L19 12 8 5.2z" /></svg>
)

const IconArrow = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M13.2 5.4 11.8 6.8 16 11H4v2h12l-4.2 4.2 1.4 1.4L19.8 12 13.2 5.4z" />
  </svg>
)

/* The overview payload mixes numbers and numeric strings ("403"). */
const num = (v) => {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

const el = (v) => num(v).toLocaleString('el-GR')
const eur = (v) => el(v) + ' €'

/* One skeleton cell, boxed to the exact height of a loaded one (62px), so the
   queue below does not shift when the numbers land. */
const AnaSkeleton = () => (
  <div className="chx-ana-sk" aria-hidden="true">
    <span className="skeleton chx-ana-sk-label"></span>
    <span className="skeleton chx-ana-sk-value"></span>
    <span className="skeleton chx-ana-sk-note"></span>
  </div>
)

const IconInbox = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M4 5.5h16a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-11a1 1 0 0 1 1-1zm6 3v7l6-3.5-6-3.5z" />
  </svg>
)

const nameOf = (row) => {
  const joined = [row && row.FirstName, row && row.LastName].filter(Boolean).join(' ').trim()
  return joined || 'Unknown user'
}

const timeOf = (row) => {
  const value = row && row.DateCreated
  const parsed = value ? moment(value) : null
  return parsed && parsed.isValid() ? parsed.valueOf() : 0
}

export default function Dashboard() {

    const [form,setForm]=useState(false)
    const [item,setItem]=useState({})
    const [isLoading,setIsLoading]=useState(true)
    const [data,setData]=useState([])
    const [sports,setSports]=useState([])
    const [failed,setFailed]=useState(false)
    const [sportFlt,setSportFlt]=useState('')
    const [flt,setFlt]=useState('')
    /* -1 = the video dialog is closed. */
    const [clipIndex,setClipIndex]=useState(-1)

    /* Analytics strip — its own state, so nothing here can reach the queue. */
    const [ana,setAna]=useState(null)
    const [anaLoading,setAnaLoading]=useState(true)
    const [anaFailed,setAnaFailed]=useState(false)
    const [growth,setGrowth]=useState([])
    const [growthLoading,setGrowthLoading]=useState(true)
    const [growthFailed,setGrowthFailed]=useState(false)


    useEffect(()=>{
        setIsLoading(true)
        loadData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[form])

    /* Once, on mount. Opening and closing the review form must not re-fetch
       analytics — it is not part of the review loop. */
    useEffect(()=>{
        let alive=true

        /* Analytics is the administrator's; the server refuses it to staff
           accounts. Asking anyway would only fill the console with refusals,
           so for staff the strip is simply not there. */
        if (sessionStorage.getItem('isAdmin')!=1){ // eslint-disable-line eqeqeq
            setAnaFailed(true)
            return undefined
        }

        axios.get(process.env.REACT_APP_API_SERVER+'admin/analytics/overview',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
            if (!alive) return
            const body=res && res.data
            if (body && Number(body.err)===0 && body.results){
              setAna(body.results)
              setAnaFailed(false)
            } else {
              setAnaFailed(true)
            }
            setAnaLoading(false)
        }).catch(err=>{
            if (!alive) return
            setAnaFailed(true)
            setAnaLoading(false)
            console.log(err)
        })

        axios.get(process.env.REACT_APP_API_SERVER+'admin/analytics/growth?months=6',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
            if (!alive) return
            const body=res && res.data
            if (body && Number(body.err)===0 && Array.isArray(body.results)){
              setGrowth(body.results)
              setGrowthFailed(false)
            } else {
              setGrowthFailed(true)
            }
            setGrowthLoading(false)
        }).catch(err=>{
            if (!alive) return
            setGrowthFailed(true)
            setGrowthLoading(false)
            console.log(err)
        })

        return ()=>{ alive=false }
    },[])


    const loadData=()=>{
        setIsLoading(true)
        setFailed(false)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/dashboard',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
         //console.log(res.data.results)
          setData(res.data.results)
          loadSports()
          setIsLoading(false)

        }).catch(err=>{
            setIsLoading(false)
            setFailed(true)
            console.log(err)
        })
      }


      const loadSports=()=>{
        setIsLoading(true)
        axios.get(process.env.REACT_APP_API_SERVER+'admin/sports',{
          headers: {
              'Authorization': 'Bearer '+sessionStorage.getItem("apiToken")
              }
          })
        .then(res=>{
           // console.log(res.data.results)
          setSports(res.data.results)
          setIsLoading(false)

        }).catch(err=>{
            setIsLoading(false)
            console.log(err)
        })
      }

    const showForm=(row,a)=>{
        setForm(a)
        setItem(row)
    }

    /* ---------------------------------------------------------------
       Render-side derivations only. Nothing here touches the requests.
       --------------------------------------------------------------- */
    const rows=useMemo(()=>(Array.isArray(data) ? data : []),[data])
    const sportRows=useMemo(()=>(Array.isArray(sports) ? sports : []),[sports])

    const sportName=useMemo(()=>{
      const map={}
      sportRows.forEach(sport=>{ map[String(sport.SportID)]=sport.SportName })
      return map
    },[sportRows])

    /* Newest first: a reviewer works the top of the pile. */
    const queue=useMemo(()=>{
      const list=rows.slice().sort((a,b)=>timeOf(b)-timeOf(a))
      const needle=flt.trim().toUpperCase()

      return list.filter(row=>{
        if (sportFlt.length>0 && String(row.SportID)!==String(sportFlt)) return false
        if (needle.length===0) return true
        const hay=(nameOf(row)+' '+(row.Title||'')).toUpperCase()
        return hay.indexOf(needle)>-1
      })
    },[rows,flt,sportFlt])

    const counts=useMemo(()=>{
      const map={}
      rows.forEach(row=>{
        const key=String(row.SportID)
        map[key]=(map[key]||0)+1
      })
      return map
    },[rows])

    const todayCount=useMemo(()=>{
      const start=moment().startOf('day').valueOf()
      return rows.filter(row=>timeOf(row)>=start).length
    },[rows])

    const withVideo=useMemo(()=>rows.filter(row=>!!row.FileID).length,[rows])

    /* Months with no activity are absent from the growth payload; a line drawn
       straight across a hole lies about the shape, so the window is filled to a
       continuous six months ending on the current one. */
    const sparkRows=useMemo(()=>fillMonthGaps(growth,{
      xKey:'ym',
      months:6,
      end:moment().format('YYYY-MM'),
      keys:['registrations']
    }),[growth])

    /* Four figures. The abandoned-checkout value is carried instead of the
       subscription count: it is the larger number and the one that is still
       open. */
    const anaTiles=useMemo(()=>{
      if (!ana) return []
      return [
        { label:'Users',           value:num(ana.totalUsers),  note:el(ana.activeUsers)+' active' },
        { label:'MAU',            value:num(ana.mau),         note:'WAU '+el(ana.wau)+' · DAU '+el(ana.dau) },
        { label:'New this month',      value:num(ana.newThisMonth),note:'Prev. month '+el(ana.newLastMonth) },
        { label:'Abandoned', value:eur(ana.revenueAbandoned), note:el(ana.abandonedOrders)+' unpaid' },
      ]
    },[ana])

    /* The dialog walks the filtered queue, in the order the reviewer sees it. */
    const clips=useMemo(()=>queue.filter(row=>!!row.FileID),[queue])
    const open=clipIndex>=0 && clipIndex<clips.length

    useEffect(()=>{
      if (clipIndex>=clips.length) setClipIndex(-1)
    },[clips.length,clipIndex])

    const playClip=(row)=>{
      const index=clips.indexOf(row)
      if (index>-1) setClipIndex(index)
    }

    const thumb=(row)=>{
      const poster=row.FileID ? posterUrl(row.FileID) : ''
      const label=nameOf(row)

      return (
        <button
          type="button"
          className="chx-thumb chx-thumb-sm"
          onClick={()=>playClip(row)}
          disabled={!row.FileID}
          title={row.FileID ? 'Play video' : 'No video'}
          aria-label={row.FileID ? 'Play video — '+label : 'No video — '+label}
        >
          {poster
            ? <img src={poster} alt="" loading="lazy" decoding="async"/>
            : <span className="chx-thumb-blank">No video</span>}
          {row.FileID && <span className="chx-play" aria-hidden="true"><IconPlay/></span>}
        </button>
      )
    }

    const queueList=queue.map((row,index)=>{
      const when=moment(row.DateCreated)
      const key=(row.UUID || row.FileID || 'row')+'-'+index

      return (
        <li key={key}>
          <div className="chx-item">
            {thumb(row)}

            <div className="chx-meta">
              <span className="chx-name">{nameOf(row)}</span>
              <span className="chx-sub">{row.Title}</span>
              <span className="chx-when">
                {when.isValid() ? when.format('DD/MM/YYYY HH:mm') : ''}
                {sportName[String(row.SportID)] ? ' · '+sportName[String(row.SportID)] : ''}
              </span>
            </div>

            <div className="chx-item-act">
              <Btn variant="accent" onClick={()=>{showForm(row,true)}}>Review</Btn>
            </div>
          </div>
        </li>
      )
    })

    const chips=(
      <div className="chx-chips" role="group" aria-label="Sport filter">
        <button
          type="button"
          className="chx-chip"
          aria-pressed={sportFlt===''}
          onClick={()=>setSportFlt('')}
        >
          All
          <span className="chx-chip-count">{rows.length}</span>
        </button>

        {sportRows.map(sport=>(
          <button
            key={sport.SportID}
            type="button"
            className="chx-chip"
            aria-pressed={String(sportFlt)===String(sport.SportID)}
            onClick={()=>setSportFlt(String(sport.SportID))}
          >
            {sport.SportName}
            <span className="chx-chip-count">{counts[String(sport.SportID)]||0}</span>
          </button>
        ))}
      </div>
    )

    if (form){
      return (
        <div className="chx-page">
          <Panel
            title="Review submission"
            subtitle="Publish or send back to the queue"
            actions={<Btn onClick={()=>{showForm(0,false)}}>Back to queue</Btn>}
          >
            <ChallengeFormReview item={item} showForm={showForm} setForm={setForm} loadData={loadData}/>
          </Panel>
        </div>
      )
    }

    const strip=(
      <section className="chx-ana" aria-label="Platform summary">
        <div className="chx-ana-body">

          <div className="chx-ana-figs">
            <span className="mono chx-ana-eyebrow">Platform</span>

            <div className="chx-ana-metrics">
              {anaLoading
                ? [0,1,2,3].map(i=><AnaSkeleton key={'ask-'+i}/>)
                : anaTiles.map((tile,index)=>(
                    <StatTile
                      key={tile.label}
                      index={index}
                      label={tile.label}
                      value={tile.value}
                      note={tile.note}
                    />
                  ))}
            </div>

            <p className="chx-ana-cap">
              Platform figures — they do not relate to the review queue.
            </p>
          </div>

          <div className="chx-ana-spark">
            <div className="chx-ana-spark-head">
              <span className="mono chx-ana-spark-title">Registrations · 6 months</span>
              <Link className="chx-ana-more" to="/analytics">
                Full analytics
                <IconArrow/>
              </Link>
            </div>

            <div className="chx-ana-plot">
              <TimeChart
                data={growthFailed ? [] : sparkRows}
                series={[{ key:'registrations', label:'Registrations', color:'--mark' }]}
                xKey="ym"
                height={96}
                loading={growthLoading}
                empty="No registration data."
              />
            </div>
          </div>

        </div>
      </section>
    )

    return (
        <div className="chx-page">

          {!anaFailed && strip}

          <div className="scoreboard">
            <div className="sb-grid">
              <StatTile index={0} label="To review" value={rows.length} note="User submissions waiting"/>
              <StatTile index={1} label="Today" value={todayCount} note="Arrived today"/>
              <StatTile index={2} label="With video" value={withVideo} note="Ready to play"/>
            </div>
          </div>

          <Panel
            title="Review Queue"
            subtitle={isLoading
              ? 'Loading…'
              : queue.length+' of '+rows.length+' submissions · newest first'}
          >
            <div className="chx-form">

              <div className="chx-toolbar">
                <div className="chx-field">
                  <label className="chx-label" htmlFor="dash-search">Search</label>
                  <input
                    id="dash-search"
                    className="chx-input"
                    type="search"
                    value={flt}
                    onChange={(e)=>setFlt(e.target.value)}
                    placeholder="User or challenge…"
                  />
                </div>

                <div className="chx-toolbar-actions">
                  <Btn onClick={()=>{setFlt(''); setSportFlt('')}}>Clear</Btn>
                  <Btn onClick={loadData}>Refresh</Btn>
                </div>
              </div>

              {chips}

              {isLoading && (
                <div className="chx-sk">
                  <div className="skeleton chx-sk-line"></div>
                  <div className="skeleton chx-sk-line"></div>
                  <div className="skeleton chx-sk-line chx-sk-short"></div>
                </div>
              )}

              {!isLoading && failed && (
                <div className="chx-note chx-note-crit" role="alert">
                  <span className="chx-note-text">
                    <b>The queue could not be loaded.</b> The server did not respond. Nothing has been lost — please try again.
                  </span>
                  <Btn onClick={loadData}>Try again</Btn>
                </div>
              )}

              {!isLoading && !failed && queue.length===0 && (
                <div className="chx-empty">
                  <span className="chx-empty-mark" aria-hidden="true"><IconInbox/></span>
                  <h2 className="chx-empty-title">
                    {rows.length===0 ? 'No submissions waiting' : 'No results'}
                  </h2>
                  <p className="chx-empty-text">
                    {rows.length===0
                      ? 'As soon as a user uploads a video for a challenge it appears here for review and publishing.'
                      : 'No results match these filters. Clear the search or pick another sport.'}
                  </p>
                </div>
              )}

              {!isLoading && !failed && queue.length>0 && (
                <ul className="chx-queue">{queueList}</ul>
              )}

            </div>
          </Panel>

          {open && (
            <VideoModal
              items={clips.map(row=>({
                fileId: row.FileID,
                name: nameOf(row),
                subtitle: row.Title
              }))}
              index={clipIndex}
              title="Review Queue"
              onIndex={setClipIndex}
              onClose={()=>setClipIndex(-1)}
            />
          )}

        </div>
    )
}
